import {
  appendSubmissionEvent,
  createTransmission,
  createTransmissionRecord,
  enrichRecordWithTransmission,
  findChildTransmissions,
  findEventsByTransmission,
  findRecordsByClient,
  findRecordsByTransmission,
  findTransmission,
  findTransmissionsByAdmin,
  updateTransmission,
} from './mockStore';
import {
  scheduleLifecycleAdvancement,
  schedulePartnerTransmission,
} from './lifecycleSimulator';

// Deterministic 64-char hex digest for demo display. Not cryptographic — a
// simple LCG (MINSTD) seeded from the id produces a stable, realistic-looking
// payload hash per transmission.
const fakePayloadHash = (seed) => {
  let state = 0;
  for (let i = 0; i < seed.length; i += 1) {
    state = (state * 31 + seed.charCodeAt(i)) % 2147483647;
  }
  let x = state + 1;
  let hex = '';
  while (hex.length < 64) {
    x = (x * 48271) % 2147483647;
    hex += (x % 16).toString(16);
  }
  return `sha256:${hex}`;
};

const paginate = (rows, limit, offset) => {
  const safeLimit = Number.isFinite(limit) ? limit : rows.length;
  const safeOffset = Number.isFinite(offset) ? offset : 0;
  return {
    records: rows.slice(safeOffset, safeOffset + safeLimit),
    totalCount: rows.length,
    limit: safeLimit,
    offset: safeOffset,
  };
};

// Create CARF records for a transmission. With a real client preview, one
// record per previewed client (real id + name); otherwise synthetic placeholders.
const materializeRecords = (transmissionId, { clients, scopedClientCount } = {}) => {
  const preview = Array.isArray(clients)
    ? clients.filter((c) => c?.clientAccountId)
    : [];
  if (preview.length) {
    preview.forEach((c) =>
      createTransmissionRecord({
        transmissionId,
        clientAccountId: c.clientAccountId,
        clientName: c.clientName,
      }),
    );
    return;
  }
  const count = Math.max(0, scopedClientCount || 0);
  for (let i = 0; i < count; i += 1) {
    createTransmissionRecord({
      transmissionId,
      clientAccountId: `client-${i + 1}`,
      clientName: `Demo Client ${i + 1}`,
    });
  }
};

const buildMockValidationSummary = (recordCount) => ({
  errorCount: 0,
  warningCount: Math.min(3, Math.floor(recordCount / 2)),
  recordCount,
  __typename: 'CarfValidationSummary',
});

const resolvers = {
  GetCarfTransmissions: ({ adminAccountId }) => ({
    data: {
      getCarfTransmissions: findTransmissionsByAdmin(adminAccountId),
    },
  }),

  GetCarfTransmission: ({ id }) => {
    const tx = findTransmission(id);
    if (!tx) return { data: { getCarfTransmission: null } };
    return {
      data: {
        getCarfTransmission: {
          ...tx,
          childTransmissions: findChildTransmissions(id).map((c) => ({
            id: c.id,
            status: c.status,
            jurisdiction: c.jurisdiction,
            period: c.period,
            __typename: 'CarfTransmission',
          })),
        },
      },
    };
  },

  GetCarfTransmissionRecords: ({ transmissionId, limit, offset }) => ({
    data: {
      getCarfTransmissionRecords: {
        ...paginate(findRecordsByTransmission(transmissionId), limit, offset),
        __typename: 'CarfTransmissionRecordPage',
      },
    },
  }),

  GetCarfSubmissionEvents: ({ transmissionId }) => ({
    data: {
      getCarfSubmissionEvents: findEventsByTransmission(transmissionId),
    },
  }),

  GetCarfClientTransmissionRecords: ({ clientAccountId, limit, offset }) => {
    const enriched = findRecordsByClient(clientAccountId).map(
      enrichRecordWithTransmission,
    );
    return {
      data: {
        getCarfClientTransmissionRecords: {
          ...paginate(enriched, limit, offset),
          __typename: 'CarfTransmissionRecordPage',
        },
      },
    };
  },

  CreateCarfTransmissionDraft: (variables) => {
    const transmission = createTransmission({
      adminAccountId: variables.adminAccountId,
      jurisdiction: variables.jurisdiction,
      period: variables.period,
      messageTypeIndic: variables.messageTypeIndic,
      parentTransmissionId: variables.parentTransmissionId,
      status: 'draft',
    });
    appendSubmissionEvent({
      transmissionId: transmission.id,
      kind: 'draft_created',
      message: 'Draft transmission created',
    });
    return { data: { createCarfTransmissionDraft: transmission } };
  },

  BuildCarfTransmissionRecords: ({ transmissionId, scopedClientCount, clients }) => {
    const transmission = findTransmission(transmissionId);
    if (!transmission) {
      return { data: { buildCarfTransmissionRecords: null } };
    }
    let existing = findRecordsByTransmission(transmissionId);
    // For new (non-correction) drafts, materialize a record preview from the
    // admin's client list.
    if (existing.length === 0 && !transmission.parentTransmissionId) {
      materializeRecords(transmissionId, { clients, scopedClientCount });
      existing = findRecordsByTransmission(transmissionId);
    }
    // recordCount stays the full client total (records may be a preview) so the
    // wizard estimate, validation and list page all match the client list.
    // Corrections keep their actual record count.
    const recordCount = transmission.parentTransmissionId
      ? existing.length
      : Math.max(scopedClientCount || 0, existing.length);
    const updated = updateTransmission(transmissionId, {
      recordCount,
    });
    return {
      data: {
        buildCarfTransmissionRecords: {
          id: updated.id,
          status: updated.status,
          recordCount: updated.recordCount,
          __typename: 'CarfTransmission',
        },
      },
    };
  },

  ValidateCarfTransmission: ({ transmissionId }) => {
    const transmission = findTransmission(transmissionId);
    if (!transmission) {
      return { data: { validateCarfTransmission: null } };
    }
    const summary = buildMockValidationSummary(transmission.recordCount || 0);
    const updated = updateTransmission(transmissionId, {
      status: 'validated',
      validationSummary: summary,
    });
    appendSubmissionEvent({
      transmissionId,
      kind: 'validated',
      message: `Validated ${summary.recordCount} records`,
    });
    return {
      data: {
        validateCarfTransmission: {
          id: updated.id,
          status: updated.status,
          validationSummary: summary,
          __typename: 'CarfTransmission',
        },
      },
    };
  },

  TransmitCarfTransmission: ({ transmissionId, confirmationId, submittedBy }) => {
    const transmission = findTransmission(transmissionId);
    if (!transmission) {
      return { data: { transmitCarfTransmission: null } };
    }
    const submittedAt = new Date().toISOString();
    const submitter = submittedBy || 'mock-user';

    if (confirmationId) {
      // Manual submission: authority reference provided directly; mark as accepted
      const updated = updateTransmission(transmissionId, {
        status: 'accepted',
        submittedAt,
        submittedBy: submitter,
        payloadHash: fakePayloadHash(transmissionId),
        payloadUri: `mock://carf-payloads/${transmissionId}.xml`,
        authorityReference: confirmationId,
      });
      appendSubmissionEvent({
        transmissionId,
        kind: 'submitted',
        message: 'XML submitted manually to tax authority',
      });
      appendSubmissionEvent({
        transmissionId,
        kind: 'accepted',
        message: `Confirmation ID recorded: ${confirmationId}`,
      });
      return {
        data: {
          transmitCarfTransmission: {
            id: updated.id,
            status: updated.status,
            submittedAt: updated.submittedAt,
            submittedBy: updated.submittedBy,
            payloadHash: updated.payloadHash,
            payloadUri: updated.payloadUri,
            authorityReference: updated.authorityReference,
            __typename: 'CarfTransmission',
          },
        },
      };
    }

    const updated = updateTransmission(transmissionId, {
      status: 'submitted',
      submittedAt,
      submittedBy: submitter,
      payloadHash: fakePayloadHash(transmissionId),
      payloadUri: `mock://carf-payloads/${transmissionId}.xml`,
    });
    appendSubmissionEvent({
      transmissionId,
      kind: 'submitted',
      message: 'Submitted to authority',
    });
    scheduleLifecycleAdvancement(transmissionId);
    return {
      data: {
        transmitCarfTransmission: {
          id: updated.id,
          status: updated.status,
          submittedAt: updated.submittedAt,
          submittedBy: updated.submittedBy,
          payloadHash: updated.payloadHash,
          payloadUri: updated.payloadUri,
          authorityReference: updated.authorityReference || null,
          __typename: 'CarfTransmission',
        },
      },
    };
  },

  CertifyCarfPartnerSubmission: ({
    adminAccountId,
    period,
    reportingPartner,
    scopedClientCount,
    clients,
    certifiedBy,
  }) => {
    const certifiedAt = new Date().toISOString();
    const transmission = createTransmission({
      adminAccountId,
      // The partner routes per-jurisdiction filing, so a partner transmission
      // carries no single jurisdiction of its own.
      jurisdiction: null,
      period,
      messageTypeIndic: 'CARF701',
      submissionMode: 'partner',
      reportingPartner,
      // Starts in-flight; settles to 'flagged' ("Transmitted to partner") once
      // the simulated handoff completes.
      status: 'transmitting',
      certifiedAt,
      certifiedBy: certifiedBy || 'user123@carfadmin.com',
    });
    // Record preview from the client list; recordCount stays the full total.
    materializeRecords(transmission.id, { clients, scopedClientCount });
    const updated = updateTransmission(transmission.id, {
      recordCount: Math.max(
        scopedClientCount || 0,
        findRecordsByTransmission(transmission.id).length,
      ),
    });
    appendSubmissionEvent({
      transmissionId: transmission.id,
      kind: 'certified',
      message: `Data marked ready for transmission to ${reportingPartner}`,
    });
    schedulePartnerTransmission(transmission.id);
    return { data: { certifyCarfPartnerSubmission: updated } };
  },

  FileCarfCorrection: ({ parentTransmissionId, correctionRecords }) => {
    const parent = findTransmission(parentTransmissionId);
    if (!parent) {
      return { data: { fileCarfCorrection: null } };
    }
    const correction = createTransmission({
      adminAccountId: parent.adminAccountId,
      jurisdiction: parent.jurisdiction,
      period: parent.period,
      messageTypeIndic: 'CARF702',
      parentTransmissionId: parent.id,
      status: 'draft',
    });
    correctionRecords.forEach((r) => {
      createTransmissionRecord({
        transmissionId: correction.id,
        clientAccountId: r.clientAccountId,
        docTypeIndic: r.docTypeIndic,
        correlatedRecordId: r.correlatedRecordId,
        correlatedDocRefId: r.correlatedDocRefId,
      });
    });
    appendSubmissionEvent({
      transmissionId: correction.id,
      kind: 'correction_filed',
      message: `Correction drafted for parent ${parent.messageRefId}`,
    });
    return {
      data: {
        fileCarfCorrection: {
          id: correction.id,
          status: correction.status,
          parentTransmissionId: correction.parentTransmissionId,
          messageRefId: correction.messageRefId,
          __typename: 'CarfTransmission',
        },
      },
    };
  },
};

export default resolvers;
